import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { LayoutDashboard, ListChecks, Store, Settings as SettingsIcon, BookOpen, LogOut, Menu, X, Plus, MoreHorizontal, User } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useCredits } from '../lib/queries'
import { errorMessage } from '../lib/api'
import { displayCredits } from '../lib/format'
import { CURRENT_APP_KEY, GENEMO_APPS, HOME_URL, LAUNCHER_APPS, SHORT_URL, markUrl, postLogoUrl } from '../lib/genemoApps'
import { LegalFooter } from './LegalFooter'

const NAV = [
  { to: '/', label: 'ダッシュボード', icon: LayoutDashboard, end: true },
  { to: '/queue', label: '承認キュー', icon: ListChecks, end: false },
  { to: '/personas', label: 'ペルソナ', icon: Store, end: false },
  { to: '/settings', label: '設定', icon: SettingsIcon, end: false },
  { to: '/manual', label: 'マニュアル', icon: BookOpen, end: false },
]

const postApp = GENEMO_APPS.find(a => a.key === CURRENT_APP_KEY)!

/** 共通シェル「パール」（DESIGN-SYSTEM.md の Shell 節・Home の AppShell と同じ器）: 上のバー（残高常設）＋左の白い板（狭い画面はドロワー） */
export function AppShell({ children }: { children: React.ReactNode }) {
  // 生成・投稿でクレジットは非同期に減るため定期更新（short の useCreditsQuery と同値）
  const credit = useCredits({ refetchInterval: 30_000, staleTime: 10_000 })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()
  const menuButton = useRef<HTMLButtonElement>(null)
  const drawer = useRef<HTMLDivElement>(null)
  const main = useRef<HTMLElement>(null)
  const balance = credit.data ? displayCredits(credit.data.balance).toLocaleString() : '—'
  // ドロワー内のリンクを選んだら（同じ画面を選んだ場合も）閉じて、フォーカスを本文へ移す。
  // 本文の inert が DOM から外れた後でないとフォーカスできないため、閉じた後の Effect で移す（Codex 3巡目）
  const [focusMainAfterClose, setFocusMainAfterClose] = useState(false)
  const closeToMain = () => { setDrawerOpen(false); setFocusMainAfterClose(true) }
  useEffect(() => {
    if (!drawerOpen && focusMainAfterClose) {
      main.current?.focus()
      setFocusMainAfterClose(false)
    }
  }, [drawerOpen, focusMainAfterClose])
  useEffect(() => setDrawerOpen(false), [location.pathname])
  // md 以上へ広がったらドロワー状態を解除する（開いたまま広げると本文が inert のまま操作不能になっていた）
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const onChange = () => { if (mq.matches) setDrawerOpen(false) }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // ドロワー（狭い画面）: 開いたら中へフォーカスを移し、Escape で閉じてメニューボタンへ戻す。
  // 開いている間は本文を inert にして、背後の操作へ Tab で抜けないようにする（Codex デザインレビュー）
  const closeDrawer = (restoreFocus: boolean) => {
    setDrawerOpen(false)
    if (restoreFocus) menuButton.current?.focus()
  }
  useEffect(() => {
    if (!drawerOpen) return
    drawer.current?.querySelector<HTMLElement>('a, button')?.focus()
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(true) }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [drawerOpen])

  return (
    <div className="gm-shell flex h-full flex-col">
      <header className="gm-topbar shrink-0">
        <button ref={menuButton} className="gm-icon-btn -ml-2 h-11 w-11 md:hidden" onClick={() => setDrawerOpen(v => !v)}
          aria-label={drawerOpen ? 'メニューを閉じる' : 'メニューを開く'} aria-expanded={drawerOpen} aria-controls="app-sidebar">
          {drawerOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <NavLink to="/" className="gm-topbar-logo" aria-label="Genemo Post のトップへ">
          <img src={markUrl(postApp)} alt="Genemo Post" className="sm:!hidden" />
          <img src={postLogoUrl} alt="Genemo Post" className="!hidden sm:!block" />
        </NavLink>
        <div className="gm-topbar-right">
          {/* Post にアカウント画面は無い。残高の行き先は Genemo Home のアカウント画面（同じタブ）。
              読み込み中・取得失敗は「—」（0 と見せない）。失敗の理由はツールチップに出す */}
          <a href={`${HOME_URL}/account`} className="gm-balance"
            aria-label={`残高 ${balance} クレジット。アカウント画面を開く`}
            title={credit.isError && !credit.data ? `残高を取得できませんでした: ${errorMessage(credit.error)}` : 'クレジット残高'}>
            <span className="gm-balance-label hidden sm:inline">残高</span>
            <span className="gm-balance-num">{balance}</span>
            <span className="gm-balance-unit">cr</span>
          </a>
          <a href={`${SHORT_URL}/account`} target="_blank" rel="noreferrer" className="gm-btn gm-btn-primary gm-btn-pill hidden sm:inline-flex">
            <Plus size={16} /> クレジットを追加
          </a>
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        {drawerOpen && <div className="fixed inset-0 z-20 bg-ink/30 md:hidden" style={{ top: 'var(--gm-shell-topbar-h)' }} onClick={() => closeDrawer(true)} />}
        <div id="app-sidebar" ref={drawer}
          onClick={e => { if (drawerOpen && (e.target as HTMLElement).closest('a')) closeToMain() }}
          className={`${drawerOpen ? 'flex' : 'hidden'} absolute inset-y-0 left-0 z-30 shrink-0 md:static md:flex`}
          style={{ padding: 'var(--gm-shell-gap)', paddingRight: 0 }}>
          <aside className="gm-sidebar h-full" aria-label="メインメニュー">
            <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
              <NavLink to="/compose" className="gm-btn gm-btn-secondary gm-btn-pill gm-side-new">
                <Plus size={16} /> 投稿を作る
              </NavLink>

              <nav className="grid gap-0.5" aria-label="画面">
                {NAV.map(({ to, label, icon: Icon, end }) => (
                  <NavLink key={to} to={to} end={end} className="gm-nav-item">
                    <Icon aria-hidden="true" />{label}
                  </NavLink>
                ))}
              </nav>

              <div className="gm-nav-heading">アプリ</div>
              <nav className="gm-nav-marks" aria-label="Genemo のほかのアプリ">
                {LAUNCHER_APPS.map(app => (
                  <a key={app.key} href={app.url} className="gm-nav-mark" data-name={app.label} aria-label={app.name}>
                    <img src={markUrl(app)} alt="" />
                  </a>
                ))}
              </nav>
            </div>

            <SideAccount />
          </aside>
        </div>

        <main ref={main} tabIndex={-1} className="flex min-w-0 flex-1 flex-col overflow-y-auto focus:outline-none" inert={drawerOpen || undefined}>
          <div className="flex-1">
            <div className="mx-auto max-w-[1200px] p-4 md:p-7">{children}</div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  )
}

/** 左下のアカウント：メール＋「⋯」でログアウト（規約のリンクは画面の一番下の gm-footer） */
function SideAccount() {
  const { user, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  // 開閉ボタン＋パネル。開いたら最初の項目へ、Escape で閉じてボタンへ戻す（Codex デザインレビュー）
  useEffect(() => {
    if (!open) return
    panel.current?.querySelector<HTMLElement>('a, button')?.focus()
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    // ドロワーの Escape（document）より先に受けて、メニューだけを閉じる
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); setOpen(false); button.current?.focus() } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onEsc, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onEsc, true)
    }
  }, [open])

  return (
    <div ref={ref} className="gm-side-account">
      {open && (
        <div id="account-panel" ref={panel} role="menu" className="gm-menu">
          <div className="gm-menu-head">{user?.email}</div>
          {/* AuthContext の logout は POST が失敗してもローカル状態を必ず消し、Protected が /login へ戻す */}
          <button role="menuitem" onClick={() => { setOpen(false); void logout() }} className="gm-menu-item gm-menu-item-danger">
            <LogOut aria-hidden="true" /> ログアウト
          </button>
        </div>
      )}
      <span className="gm-side-account-icon"><User size={16} aria-hidden="true" /></span>
      <span className="gm-side-account-mail" title={user?.email}>{user?.email}</span>
      <button ref={button} className="gm-icon-btn" onClick={() => setOpen(v => !v)} aria-haspopup="menu" aria-expanded={open} aria-controls="account-panel" aria-label="アカウントメニューを開く">
        <MoreHorizontal size={18} />
      </button>
    </div>
  )
}
